/**
 * API Client - T037
 *
 * Fetch wrapper with authentication, error handling, and typed responses
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown
  ) {
    super(`${status} ${statusText}`);
    this.name = 'ApiError';
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { body, headers: customHeaders, ...rest } = options;

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  const config: RequestInit = {
    ...rest,
    headers,
    credentials: 'include', // Include cookies for session auth
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

  // Handle no-content responses
  if (response.status === 204) {
    return undefined as T;
  }

  const data = await response.json().catch(() => null);

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, data);
  }

  return data as T;
}

// HTTP method helpers
export const api = {
  // T483: exposed so components that need to build their own URLs (e.g.
  // `<iframe src="…/preview">`, `<img>`) can use the same origin/path
  // prefix the request() helper uses for JSON calls.
  baseUrl: API_BASE_URL,

  get: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'POST', body }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PUT', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'PATCH', body }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    request<T>(endpoint, { ...options, method: 'DELETE' }),
};

// Auth-specific endpoints
export const authApi = {
  login: (email: string, password: string) =>
    api.post<{ user: { id: string; email: string; name: string } }>('/auth/sign-in/email', {
      email,
      password,
    }),

  logout: () => api.post<{ success: boolean }>('/auth/sign-out', {}),

  me: () =>
    api.get<{
      user: { id: string; email: string; name: string };
      session: { expiresAt: string };
    }>('/auth/me'),
};

// Settings endpoints
export const settingsApi = {
  getAll: () => api.get<Record<string, Record<string, unknown>>>('/settings'),

  get: (id: string) => api.get<{ id: string; value: unknown }>(`/settings/${id}`),

  update: (settings: Record<string, Record<string, unknown>>) =>
    api.patch<{ message: string; updated: string[] }>('/settings', settings),
};

export default api;
